import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, X, Plus, MessageSquare, ChevronLeft, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAiChatStore } from '../../stores/aiChatStore';
import { useOrganizationStore } from '../../stores/organizationStore';
import { AiChatMessage } from './AiChatMessage';
import { AiChatInput } from './AiChatInput';
import { AiChatSidebar } from './AiChatSidebar';

const TypingIndicator: React.FC = () => (
  <div className="flex justify-start">
    <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-gray-100 dark:bg-gray-700">
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  </div>
);

export const AiChatPanel: React.FC = () => {
  const { t } = useTranslation();
  const {
    isOpen,
    closeChat,
    currentConversationId,
    messages,
    isLoading,
    error,
    fetchConversations,
    createConversation,
    sendMessage,
    clearError,
  } = useAiChatStore();

  const { activeOrganizationId } = useOrganizationStore();
  const [showSidebar, setShowSidebar] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasFetchedRef = useRef(false);

  // Reset fetch ref when active organization changes to trigger fresh queries
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [activeOrganizationId]);

  // Fetch conversations when panel opens for the first time
  useEffect(() => {
    if (isOpen && !hasFetchedRef.current) {
      fetchConversations();
      hasFetchedRef.current = true;
    }
  }, [isOpen, fetchConversations]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Close sidebar on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closeChat();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeChat]);

  const handleNewConversation = async () => {
    await createConversation();
    setShowSidebar(false);
  };

  const handleSend = async (content: string) => {
    await sendMessage(content);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 md:hidden"
        onClick={closeChat}
      />

      <div
        className="
          fixed z-50
          bottom-0 right-0 md:bottom-6 md:right-6
          w-[calc(100vw-2rem)] sm:w-[400px] md:w-[400px]
          h-full md:h-[600px] md:max-h-[calc(100vh-3rem)]
          md:rounded-2xl md:shadow-2xl
          bg-white dark:bg-gray-800
          border border-gray-200 dark:border-gray-700
          flex flex-col
          transition-transform duration-300 ease-out
          pb-[env(safe-area-inset-bottom)]
        "
        style={{ animation: '.3s ease-out' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label={t('aiChat.panel.toggleList')}
            >
              <MessageSquare className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
            <div className="flex items-center gap-1.5">
              <Sparkles className="w-5 h-5 text-indigo-500" />
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('aiChat.panel.title')}</h2>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleNewConversation}
              className="p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label={t('aiChat.panel.newConversation')}
            >
              <Plus className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
            <button
              onClick={closeChat}
              className="p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label={t('aiChat.panel.closeChat')}
            >
              <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Error bar */}
        {error && (
          <div className="mx-3 mt-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button onClick={clearError} className="text-red-500 hover:text-red-700 dark:hover:text-red-400">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Main content area */}
        <div className="flex-1 flex overflow-hidden relative">
          {/* Sidebar overlay */}
          {showSidebar && (
            <div className="absolute inset-0 z-10 flex">
              <div className="w-full max-w-80 md:w-[260px] bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col h-full">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('aiChat.panel.conversations')}</h3>
                  <button
                    onClick={() => setShowSidebar(false)}
                    className="p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    aria-label={t('aiChat.panel.closeSidebar')}
                  >
                    <ChevronLeft className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
                <AiChatSidebar onClose={() => setShowSidebar(false)} />
              </div>
              <div
                className="hidden md:block flex-1 bg-black/10"
                onClick={() => setShowSidebar(false)}
              />
            </div>
          )}

          {/* Messages area */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Empty state */}
            {!currentConversationId && messages.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
                <Sparkles className="w-12 h-12 text-indigo-300 dark:text-indigo-600 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  {t('aiChat.panel.empty.title')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  {t('aiChat.panel.empty.subtitle')}
                </p>
                <div className="grid grid-cols-1 gap-2 w-full max-w-xs">
                  {[
                    t('aiChat.suggestion.focusToday'),
                    t('aiChat.suggestion.planWeek'),
                    t('aiChat.suggestion.summarizePending'),
                  ].map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => handleSend(suggestion)}
                      className="px-3 py-2 text-sm text-left rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {messages.map((msg) => (
                  <AiChatMessage key={msg.id} message={msg} />
                ))}
                {isLoading && <TypingIndicator />}
                <div ref={messagesEndRef} />
              </div>
            )}

            {/* Input area */}
            <AiChatInput onSend={handleSend} isLoading={isLoading} />
          </div>
        </div>
      </div>
    </>
  );
};

export default AiChatPanel;
